import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import LocalText from '../components/common/LocalText';
import ContentArea from '../components/content-area/Content';
import AdvertiserAdTable from '../components/advertisers/AdvertiserAdTable';
import { getCampaignById } from '../api/advertiserApi';
import { useLocalization } from '../context/LocalizationContext';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

const AdvertiserCampaignPage = () => {
  const { brandId, campaignId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { locale } = useLocalization();
  const [campaign, setCampaign]       = useState(state?.campaign ?? null);
  const [loading, setLoading]         = useState(!state?.campaign);
  const advertiserApprovalStatus      = state?.advertiser?.approvalStatus ?? null;
  const overrideSpaceId               = state?.overrideSpaceId ?? null;

  useEffect(() => {
    if (state?.campaign) return;
    setLoading(true);
    getCampaignById(Number(brandId), Number(campaignId), overrideSpaceId)
      .then(setCampaign)
      .catch(() => setCampaign(null))
      .finally(() => setLoading(false));
  }, [brandId, campaignId, state?.campaign, overrideSpaceId]);

  // Must be before any early return — hooks cannot be called conditionally.
  // When campaign is null the value is unused; the early return below handles that.
  const campaignWithAdvertiser = useMemo(
    () => campaign ? { ...campaign, advertiserId: Number(brandId) } : null,
    [campaign, brandId],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress sx={{ color: '#0a326d' }} />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="40vh" gap={2}>
        <Typography variant="h6" sx={{ color: '#85a5c6' }}><LocalText>Campaign not found.</LocalText></Typography>
        <Button variant="outlined" onClick={() => navigate(`/advertisers/${brandId}`)} sx={{ textTransform: 'none', borderRadius: '10px', color: '#0a326d', borderColor: '#0a326d' }}>
          <LocalText>Back to Campaigns</LocalText>
        </Button>
      </Box>
    );
  }

  return (
    <ContentArea
      title={campaign.localizedName?.values?.find((v) => v.localeId === locale)?.value || campaign.localizedName?.values?.[0]?.value || 'Ads'}
      icon={<CampaignOutlinedIcon sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />}
      container={false}
    >
      <AdvertiserAdTable campaign={campaignWithAdvertiser} advertiserApprovalStatus={advertiserApprovalStatus} overrideSpaceId={overrideSpaceId} />
    </ContentArea>
  );
};

export default AdvertiserCampaignPage;
